import * as React from 'react'
import { createPortal } from 'react-dom'
import * as classNames from 'classnames'
import DatePicker from 'react-datepicker'
import {
  addDays,
  getYear,
  getMonth,
  getDay,
  getWeeksInMonth,
  lastDayOfMonth,
  addMonths,
  subMonths,
} from 'date-fns'
import ja from 'date-fns/locale/ja'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'

import { ModalWrapper } from '~/atoms/modal/ModalWrapper'
import { dateToStringWithFormat } from '@/utils/dateFormat'

import * as Style from './style.css'
import 'react-datepicker/dist/react-datepicker.css'

export interface IProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'type' | 'className'> {
  isInitialized?: boolean
  defaultDate?: Date
  inputDate: Date
  minDate?: Date
  maxDate?: Date
  isOpen: boolean
  setDateHandler: (date: Date) => void
  setTodayHandler: (date: Date) => void
  setTomorrowHandler: (date: Date) => void
  resetHandler: () => void
  submitHandler: () => void
  openHandler: () => void
  closeHandler: () => void
}

const LABEL_FORMAT = 'yyyy-MM-dd'

export const InputDate = ({
  isInitialized,
  name,
  defaultDate,
  inputDate,
  minDate,
  maxDate,
  isOpen,
  readOnly,
  onChange,
  setDateHandler,
  setTodayHandler,
  setTomorrowHandler,
  resetHandler,
  submitHandler,
  openHandler,
  closeHandler,
  ...props
}: IProps): JSX.Element => {
  const startDate = !!minDate ? minDate : new Date()
  const endDate = !!maxDate ? maxDate : addMonths(new Date(), 6)
  const today = new Date()
  const tomorrow = addDays(new Date(), 1)
  const years: number[] = []

  for (let i = startDate.getFullYear(); i <= endDate.getFullYear(); i++) {
    years.push(i)
  }
  const MONTHS: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
  const [isYearEdit, setYearEdit] = React.useState(false)
  const [isMonthEdit, setMonthEdit] = React.useState(false)
  const [forceUpdateCount, setForceUpdateCount] = React.useState(0)
  const [isForceUpdateCountUp, setIsForceUpdateCountUp] = React.useState(false)
  const [currentCalendarDate, setCurrentCalendarDate] = React.useState<Date>(
    typeof inputDate !== 'undefined' ? inputDate : minDate,
  )
  const isNextMonthRowUpCss = getDay(lastDayOfMonth(currentCalendarDate)) !== 6
  const isNextMonth2weekhideCss =
    getWeeksInMonth(lastDayOfMonth(currentCalendarDate)) === 6 ||
    (getWeeksInMonth(lastDayOfMonth(currentCalendarDate)) === 5 &&
      getDay(lastDayOfMonth(currentCalendarDate)) === 6)
  const currentCalendarDateHandler = (date: Date) => {
    setCurrentCalendarDate(date)
  }
  const setDateAfterEventHandler = (date: Date) => {
    setDateHandler(date)
    setCurrentCalendarDate(date)
  }
  const clickEditBackgroundHandler = () => {
    setYearEdit(false)
    setMonthEdit(false)
  }
  const forceUpdateCountUp = () => {
    const nextCount = forceUpdateCount
    setForceUpdateCount(nextCount + 1)
  }

  /*-------------- iOS16のレンダリング漏れ対策 --------------*/
  React.useEffect(() => {
    setIsForceUpdateCountUp(true)
    const timeoutId = setTimeout(() => {
      setIsForceUpdateCountUp(false)
    }, 200)
    return () => {
      clearTimeout(timeoutId)
    }
  }, [forceUpdateCount])

  return (
    <>
      <p
        className={
          !readOnly ? Style.InputDateLabel : Style.InputDateLabelReadOnly
        }
        onClick={!readOnly ? openHandler : void 0}
      >
        {!!defaultDate ? dateToStringWithFormat(defaultDate, LABEL_FORMAT) : ''}
      </p>
      {!readOnly && (
        <>
          {isInitialized &&
            createPortal(
              <ModalWrapper mobileAnimation="fadeIn" isOpen={isOpen}>
                <div className={Style.InputDateWrapper}>
                  <div className={Style.InputDate}>
                    <FontAwesomeIcon
                      icon={faXmark}
                      className={Style.InputDateClose}
                      onClick={closeHandler}
                    />
                    <div
                      className={classNames({
                        [Style.InputDatePicker]: true,
                        [Style.NextMonthRowUpCss]: isNextMonthRowUpCss,
                        [Style.NextMonth2weekhideCss]: isNextMonth2weekhideCss,
                        [Style.ForceUpdateCss]: isForceUpdateCountUp,
                      })}
                    >
                      <DatePicker
                        onChange={() => void 0}
                        selected={inputDate}
                        onSelect={(date: Date) => {
                          setDateAfterEventHandler(date)
                          forceUpdateCountUp()
                        }}
                        locale={ja}
                        minDate={startDate}
                        maxDate={endDate}
                        inline
                        monthsShown={2}
                        focusSelectedMonth={true}
                        renderCustomHeader={({
                          date,
                          changeYear,
                          changeMonth,
                          decreaseMonth,
                          increaseMonth,
                          prevMonthButtonDisabled,
                          nextMonthButtonDisabled,
                        }) => (
                          <div>
                            <div className={Style.InputDateSelecter}>
                              <span
                                className={Style.InputDatePickerSetDate}
                                onClick={() => {
                                  changeYear(getYear(today))
                                  changeMonth(getMonth(today))
                                  setTodayHandler(today)
                                  currentCalendarDateHandler(today)
                                  forceUpdateCountUp()
                                }}
                              >
                                今日
                              </span>
                              <span
                                className={Style.InputDatePickerSetDate}
                                onClick={() => {
                                  changeYear(getYear(tomorrow))
                                  changeMonth(getMonth(tomorrow))
                                  setTomorrowHandler(tomorrow)
                                  currentCalendarDateHandler(tomorrow)
                                  forceUpdateCountUp()
                                }}
                              >
                                明日
                              </span>
                            </div>
                            <div className={'datepicker__header__dropdown'}>
                              <button
                                type="button"
                                className={
                                  'react-datepicker__navigation react-datepicker__navigation--previous'
                                }
                                aria-label="Previous Month"
                                onClick={() => {
                                  decreaseMonth()
                                  currentCalendarDateHandler(subMonths(date, 1))
                                  forceUpdateCountUp()
                                }}
                                style={
                                  prevMonthButtonDisabled
                                    ? { visibility: 'hidden' }
                                    : null
                                }
                              >
                                <span
                                  className={
                                    'react-datepicker__navigation-icon react-datepicker__navigation-icon--previous'
                                  }
                                >
                                  Previous Month
                                </span>
                              </button>
                              <button
                                type="button"
                                className={
                                  'react-datepicker__navigation react-datepicker__navigation--next'
                                }
                                aria-label="Next Month"
                                onClick={() => {
                                  increaseMonth()
                                  currentCalendarDateHandler(addMonths(date, 1))
                                  forceUpdateCountUp()
                                }}
                                style={
                                  nextMonthButtonDisabled
                                    ? { visibility: 'hidden' }
                                    : null
                                }
                              >
                                <span
                                  className={
                                    'react-datepicker__navigation-icon react-datepicker__navigation-icon--next'
                                  }
                                >
                                  Next Month
                                </span>
                              </button>
                              <div className={Style.InputDateDropdownYear}>
                                <span
                                  className={Style.InputDateDropdownYearLabel}
                                  onClick={() => {
                                    setYearEdit(true)
                                    forceUpdateCountUp()
                                  }}
                                >
                                  {getYear(date)}年
                                </span>
                                <div
                                  className={
                                    isYearEdit
                                      ? Style.DateDropdownWrapperYearEdit
                                      : Style.DateDropdownWrapper
                                  }
                                >
                                  {years.map((year, index) => (
                                    <span
                                      className={
                                        year == getYear(date)
                                          ? Style.InputDateDropdownSelected
                                          : Style.InputDateDropdown
                                      }
                                      onClick={() => {
                                        changeYear(year)
                                        setYearEdit(false)
                                        date.setFullYear(year)
                                        currentCalendarDateHandler(date)
                                        forceUpdateCountUp()
                                      }}
                                      key={`dropdown-year-index-${index}`}
                                    >
                                      {year}年
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className={Style.InputDateDropdownMonth}>
                                <span
                                  className={Style.InputDateDropdownMonthLabel}
                                  onClick={() => setMonthEdit(true)}
                                >
                                  {getMonth(date) + 1}月
                                </span>
                                <div
                                  className={
                                    isMonthEdit
                                      ? Style.DateDropdownWrapperMonthEdit
                                      : Style.DateDropdownWrapper
                                  }
                                >
                                  {MONTHS.map((month: number, index) => (
                                    <span
                                      className={
                                        month == getMonth(date) + 1
                                          ? Style.InputDateDropdownSelected
                                          : Style.InputDateDropdown
                                      }
                                      onClick={() => {
                                        changeMonth(MONTHS.indexOf(month))
                                        setMonthEdit(false)
                                        date.setMonth(MONTHS.indexOf(month))
                                        currentCalendarDateHandler(date)
                                        forceUpdateCountUp()
                                      }}
                                      key={`dropdown-month-index-${index}`}
                                    >
                                      {month}月
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      />
                      <div className={Style.InputDateActions}>
                        <span
                          className={Style.InputDateReset}
                          onClick={resetHandler}
                        >
                          リセット
                        </span>
                        <span
                          className={Style.InputDateSubmit}
                          onClick={submitHandler}
                        >
                          決定
                        </span>
                      </div>
                    </div>
                    {(isYearEdit || isMonthEdit) && (
                      <span
                        className={Style.InputDateBackground}
                        onClick={clickEditBackgroundHandler}
                      ></span>
                    )}
                  </div>
                </div>
              </ModalWrapper>,
              document.body,
            )}
        </>
      )}
    </>
  )
}
